/**
 * Portal Course Group Teacher Assignment API Client
 *
 * Handles operations for teacher assignments to course groups with Cognito authentication.
 */

// API Gateway URL
const API_BASE_URL = 'https://jrgfnay5w2.execute-api.ap-southeast-2.amazonaws.com/v1';

/**
 * Get the authentication token from the user's session.
 */
const getAuthToken = () => {
  const oidcStorage = sessionStorage.getItem('oidc.user:https://cognito-idp.ap-southeast-2.amazonaws.com/ap-southeast-2_3n9j6gDzL:2c6na652k2lkcsaa1ol8m5chp4');
  if (oidcStorage) {
    try {
      const oidcData = JSON.parse(oidcStorage);
      return oidcData.id_token || oidcData.access_token;
    } catch (e) {
      console.error('Error parsing OIDC storage:', e);
    }
  }
  return null;
};

/**
 * Make an authenticated API request to course group teacher endpoint.
 */
const apiRequest = async (operation, data = {}) => {
  const token = getAuthToken();

  if (!token) {
    throw new Error('Not authenticated. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}/course-group-teacher`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      operation,
      data
    })
  });

  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || `Request failed with status ${response.status}`);
  }

  return result;
};

// ==================== Teacher Assignment API ====================

/**
 * List teachers assigned to a course group.
 *
 * @param {string} courseGroupId - Course group ID
 */
export const listTeachers = async (courseGroupId) => {
  const result = await apiRequest('list', { courseGroupId });
  return result.teachers || [];
};

/**
 * List course groups for the current teacher.
 * Only works for users with portal_teacher role.
 */
export const listByTeacher = async () => {
  const result = await apiRequest('listByTeacher', {});
  return result.courseGroups || [];
};

/**
 * Assign a teacher to a course group with optional subject assignments.
 *
 * @param {string} courseGroupId - Course group ID
 * @param {string} teacherId - Teacher's UserPortal ID
 * @param {boolean} isPrimary - Whether this is the primary teacher
 * @param {string[]} subjectIds - Optional array of subject IDs to assign to the teacher
 */
export const addTeacher = async (courseGroupId, teacherId, isPrimary = false, subjectIds = []) => {
  const result = await apiRequest('add', { courseGroupId, teacherId, isPrimary, subjectIds });
  return result.assignment;
};

/**
 * Update a teacher assignment (isPrimary, subjectIds).
 *
 * @param {string} assignmentId - Assignment ID
 * @param {Object} updates - Fields to update { isPrimary, subjectIds }
 */
export const updateAssignment = async (assignmentId, updates) => {
  const result = await apiRequest('update', { assignmentId, ...updates });
  return result.assignment;
};

/**
 * Update a teacher's subject assignments.
 *
 * @param {string} assignmentId - Assignment ID
 * @param {string[]} subjectIds - Array of subject IDs to assign
 */
export const updateTeacherSubjects = async (assignmentId, subjectIds) => {
  const result = await apiRequest('update', { assignmentId, subjectIds });
  return result.assignment;
};

/**
 * Remove a teacher from a course group.
 *
 * @param {string} assignmentId - Assignment ID
 */
export const removeTeacher = async (assignmentId) => {
  const result = await apiRequest('remove', { assignmentId });
  return result;
};

/**
 * List all available teachers for assignment.
 */
export const listAvailableTeachers = async (campusId = null) => {
  const data = campusId ? { campusId } : {};
  const result = await apiRequest('listTeachers', data);
  return result.teachers || [];
};

/**
 * Register a relief (substitute) teacher for one week of a regular-term schedule.
 *
 * The base schedule's teacherId is never touched — the override only applies to the given
 * week, so materials and the management timetable both revert to the regular teacher on
 * their own once that week passes. Also grants the relief teacher a course group
 * membership if they don't already have one (for feedback/answer access).
 *
 * @param {string} courseGroupId - Course group ID
 * @param {number} scheduleIndex - Index into the course group's schedules[] array
 * @param {number} week - Week number (matches the course's assignedWeek numbering)
 * @param {string} teacherId - Relief teacher's UserPortal ID
 */
export const setScheduleRelief = async (courseGroupId, scheduleIndex, week, teacherId) => {
  const result = await apiRequest('setRelief', { courseGroupId, scheduleIndex, week, teacherId });
  return result.schedules || [];
};

/**
 * Remove a relief entry for one schedule/week (cancel a mistake or end it early).
 *
 * @param {string} courseGroupId - Course group ID
 * @param {number} scheduleIndex - Index into the course group's schedules[] array
 * @param {number} week - Week number to clear
 */
export const clearScheduleRelief = async (courseGroupId, scheduleIndex, week) => {
  const result = await apiRequest('clearRelief', { courseGroupId, scheduleIndex, week });
  return result.schedules || [];
};

const courseGroupTeacherApi = {
  listTeachers,
  listByTeacher,
  addTeacher,
  updateAssignment,
  updateTeacherSubjects,
  removeTeacher,
  listAvailableTeachers,
  setScheduleRelief,
  clearScheduleRelief
};

export default courseGroupTeacherApi;
